import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import RouterPropTypes from 'react-router-prop-types';
import { withRouter, Redirect } from 'react-router';
import {
  setWith,
  isEqual,
  get,
  cloneDeep,
  unset,
  has,
  set,
  head,
  partition
} from 'lodash';
import { Row, Col } from 'reactstrap';

import { modelOf } from '../../../prop-types';
import ProductStore from '../../../store/ProductStore';
import { parse, stringify } from '../../../util/queryString';
import FilterType from '../../../types/FilterType';
import ProductList from '../ProductList';
import FilteringOptions from '../FilteringOptions';
import ActiveFilterList from '../ActiveFilterList';
import ProductListTools from '../ProductListTools';
import ContentForState from '../../loader/ContentForState';
import Paginator from '../../common/Paginator';
import { scrollToElementById } from '../../../util/dom';
import ScrollableAnchor from '../../anchor/ScrollableAnchor';
import BooleanFilter from '../BooleanFilter';
import ConfigStore from '../../../store/ConfigStore';
import { paramsToQueryIdentifier } from '../../../util/query';
import NoProductsFound from '../NoProductsFound';
import SEOPagination from '../../head/SEOPagination';
import RobotsMeta from '../../head/RobotsMeta';
import {
  escapeUrlForPaginatorTemplate,
  escapeUrlParametersForPaginatorTemplate
} from '../../../util/url';
import ProductClass from '../../../types/ProductClass';
import RouteService from '../../../services/RouteService';
import ProductListFilter from '../ProductListFilter';
import ProductSortOrderBy from '../../../types/ProductSortOrderBy';

const ProductSortParameters = {
  ORDER_BY: 'orderBy',
  ORDER: 'order'
};

// set treats numeric keys as arrays, we use setWith to force objects
const setObject = (object, path, value) => {
  setWith(object, path, value, Object);
};

export const ANCHOR_ID = 'mainProductList';
const IN_STOCK_FILTER_KEY = 'inStock';

@observer
export class MainProductList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showAllFilters: false,
      searchTerm: ''
    };

    this.validateActiveSortParameters();
    this.loadProducts();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !isEqual(prevProps.fixedParams, this.props.fixedParams) ||
      !isEqual(prevState.searchParameters, this.getSearchParameters()) ||
      prevState.searchTerm !== this.state.searchTerm
    ) {
      this.loadProducts();
    }
    this.validateFilters();
  }

  getSearchParameters = () =>
    MainProductList.propsToSearchParameters(this.props);

  /**
   *  Check if current filters can all be found in the current search result.
   *  If not, remove them, because it means they are not longer valid.
   */
  validateFilters = () => {
    const searchParameters = this.getSearchParameters();
    const searchResult = this.getCurrentSearchResult();
    if (searchResult && searchResult.available_filters) {
      const validFilters = {};

      searchResult.available_filters.forEach((filter) => {
        const currentValue = get(searchParameters.filters, filter.key);
        if (currentValue) {
          setObject(validFilters, filter.key, currentValue);
        }
      });

      if (!isEqual(searchParameters.filters, validFilters)) {
        this.setSearchParameters({
          ...searchParameters,
          filters: validFilters
        });
      }
    }
  };

  validateActiveSortParameters = () => {
    const { configStore, productStore } = this.props;
    const productListSelectors =
      configStore.productList.productListSortSelectors;
    const searchParameters = this.getSearchParameters();

    if (
      !productListSelectors ||
      !searchParameters.sort ||
      !Object.keys(searchParameters.sort).length
    ) {
      return;
    }

    const activeProductSortSelectors = productStore.activeProductSortList();

    const validSortParameter = activeProductSortSelectors.find(
      (ss) =>
        ss.sortKey === searchParameters.sort.orderBy &&
        ss.sortOrder === searchParameters.sort.order
    );

    if (!validSortParameter) {
      searchParameters.sort = {};
      this.setSearchParameters(searchParameters);
    }
  };

  getQueryIdentifier = () => {
    let productLoadParameters = this.getProductLoadParameters();
    productLoadParameters = this.addSearchtermToProductLoadParameters(
      productLoadParameters
    );
    return paramsToQueryIdentifier(productLoadParameters);
  };

  getCurrentSearchResult = () =>
    this.props.productStore.productQueryResults.get(this.getQueryIdentifier());

  getCurrentSearchState = () =>
    this.props.productStore.productQueryStates.get(this.getQueryIdentifier());

  static propsToSearchParameters = (props) => {
    const {
      location: { search }
    } = props;

    const parsedSearchParameters = parse(search);

    return MainProductList.transformParameters(parsedSearchParameters);
  };

  setSearchParameters = (parameters) => {
    const { history } = this.props;
    history.push({
      search: stringify(
        MainProductList.transformParametersBeforeStringify({
          ...parameters,
          // Always reset page to 1 when filtering changes
          page: 1
        })
      )
    });
  };

  /**
   * Transform "f "and "s" to "filter" and "sort".
   */
  static transformParameters(parameters) {
    parameters.filters = parameters.f || {};
    delete parameters.f;
    parameters.sort = parameters.s || {};
    delete parameters.s;

    return parameters;
  }

  /**
   * Transform "filter" and "sort" to "f" and "f".
   */
  static transformParametersBeforeStringify(parameters) {
    parameters.f = parameters.filters;
    delete parameters.filters;
    parameters.s = parameters.sort;
    delete parameters.sort;
    return parameters;
  }

  getProductLoadParameters = () => {
    const { fixedParams, configStore, showCategoryFilter } = this.props;
    const searchParameters = this.getSearchParameters();

    if (!searchParameters) {
      return { ...fixedParams };
    }

    if (
      showCategoryFilter &&
      this.categoryFilterActive(searchParameters) &&
      fixedParams.cross_categories
    ) {
      delete fixedParams.cross_categories;
      return {
        inStock: configStore.inStockProductsOnly ? '1' : '0',
        page: searchParameters.page,
        ...searchParameters.filters,
        ...searchParameters.sort,
        ...fixedParams
      };
    }

    return {
      inStock: configStore.inStockProductsOnly ? '1' : '0',
      page: searchParameters.page,
      ...searchParameters.filters,
      ...searchParameters.sort,
      ...fixedParams
    };
  };

  categoryFilterActive = (searchParameters) => {
    return (
      searchParameters.filters.cross_categories &&
      searchParameters.filters.cross_categories.length > 0
    );
  };

  loadProducts = () => {
    const { productStore, allSections, urlOverride } = this.props;
    let productLoadParameters = this.getProductLoadParameters();
    productLoadParameters = this.addSearchtermToProductLoadParameters(
      productLoadParameters
    );

    if (!this.getCurrentSearchResult() && !this.getCurrentSearchState()) {
      productStore.loadProducts(
        productLoadParameters,
        allSections,
        urlOverride
      );
    }
  };

  addSearchtermToProductLoadParameters = (productLoadParameters) => {
    if (
      !this.props.configStore.productListFilter.enabled ||
      !this.state.searchTerm
    ) {
      return productLoadParameters;
    }

    productLoadParameters.text = this.state.searchTerm;
    return productLoadParameters;
  };

  onAcceptFilter = (filter, value) => {
    const searchParameters = cloneDeep(this.getSearchParameters());

    switch (filter.type) {
      case FilterType.BOOLEAN: {
        if (value) {
          setObject(searchParameters.filters, filter.key, 1);
        } else {
          unset(searchParameters.filters, filter.key);
        }
        break;
      }
      case FilterType.RANGE: {
        setObject(searchParameters.filters, filter.key, {
          min: Number(value[0]),
          max: Number(value[1])
        });
        break;
      }
      case FilterType.TERM: {
        setObject(searchParameters.filters, filter.key, value);
        break;
      }
      default: {
        console.error('Unknown filter type ' + filter.type);
      }
    }

    this.setSearchParameters(searchParameters);
  };

  onInStockAccept = (filter, inStockFilterValue) => {
    const { configStore } = this.props;
    if (configStore.inStockProductsOnly !== inStockFilterValue) {
      configStore.setInStockProductsOnly(inStockFilterValue);
    }
    this.loadProducts();
  };

  onStyleSelect = (style) => {
    const { configStore } = this.props;
    configStore.setProductListStyle(style);
  };

  getActiveFilters = () => {
    const searchResult = this.getCurrentSearchResult();
    if (!searchResult || !searchResult.available_filters) {
      return [];
    }
    const searchParameters = cloneDeep(this.getSearchParameters());
    return searchResult.available_filters.reduce((activeFilters, filter) => {
      if (has(searchParameters.filters, filter.key)) {
        const filterValue = get(searchParameters.filters, filter.key);
        activeFilters.push({
          key: filter.key,
          filter,
          name: filter.name,
          value: filterValue.min || filterValue.max ? null : filterValue,
          min: filterValue.min ? Number(filterValue.min) : null,
          max: filterValue.max ? Number(filterValue.max) : null
        });
      }
      return activeFilters;
    }, []);
  };

  removeFilter = (activeFilter, valueToRemove) => {
    const searchParameters = cloneDeep(this.getSearchParameters());

    switch (activeFilter.filter.type) {
      case FilterType.TERM: {
        let values = get(searchParameters.filters, activeFilter.key);
        let indexToRemove = values.indexOf(valueToRemove);
        if (indexToRemove !== -1) values.splice(indexToRemove, 1);
        break;
      }
      case FilterType.RANGE: {
        unset(searchParameters.filters, activeFilter.key + '[min]');
        unset(searchParameters.filters, activeFilter.key + '[max]');
        break;
      }
      default: {
        unset(searchParameters.filters, activeFilter.key);
      }
    }

    this.setSearchParameters(searchParameters);
  };

  clearFilters = () => {
    this.setSearchParameters({
      ...this.getSearchParameters(),
      filters: []
    });
  };

  onSelectSortOrder = (sortOrderKey, ascDesc) => {
    const searchParameters = cloneDeep(this.getSearchParameters());
    if (sortOrderKey) {
      set(searchParameters.sort, ProductSortParameters.ORDER_BY, sortOrderKey);
    } else {
      unset(searchParameters.sort, ProductSortParameters.ORDER_BY);
    }

    if (ascDesc) {
      set(searchParameters.sort, ProductSortParameters.ORDER, ascDesc);
    } else {
      unset(searchParameters.sort, ProductSortParameters.ORDER);
    }

    this.setSearchParameters(searchParameters);
  };

  getSelectedSortOrderBy = () => {
    const searchParameters = this.getSearchParameters();
    return get(searchParameters.sort, ProductSortParameters.ORDER_BY);
  };

  getSelectedSortOrder = () => {
    const searchParameters = this.getSearchParameters();
    return get(searchParameters.sort, ProductSortParameters.ORDER);
  };

  renderProductListFilter = () => {
    const { disableQuickSearch } = this.props;
    if (disableQuickSearch) {
      return null;
    }

    return (
      <Row>
        <Col
          lg={8}
          xl={6}
          className="MainProductList__active-filter--quicksearch mr-lg-auto"
        >
          <ProductListFilter
            onProductListFiltering={this.filterProductList}
            clearProductListFilter={this.clearProductListFilter}
            searchTerm={this.state.searchTerm}
          />
        </Col>
      </Row>
    );
  };

  filterProductList = (searchTerm) => {
    if (searchTerm) {
      this.setState({ searchTerm });
    }

    if (!searchTerm && this.state.searchTerm) {
      this.clearProductListFilter();
    }
  };

  clearProductListFilter = () => {
    if (this.state.searchTerm) {
      this.setState({ searchTerm: '' });
    }
  };

  getSEOPagination = () => {
    const { location } = this.props;
    const {
      last_page: lastPage,
      current_page: currentPage
    } = this.getCurrentSearchResult();

    return (
      <SEOPagination
        currentPage={currentPage}
        lastPage={lastPage}
        path={location.pathname}
      />
    );
  };

  getPaginator = () => {
    const { location } = this.props;
    const {
      last_page: lastPage,
      current_page: currentPage
    } = this.getCurrentSearchResult();

    if (lastPage < 2) {
      return null;
    }

    let searchTemplate;
    const regex = /page=(\d+?)(?=(&|$))/;
    const pageParameter = `page=:page`;

    const urlEncodedParameters = escapeUrlParametersForPaginatorTemplate(
      location.search
    );

    if (location.search.charAt(0) !== '?') {
      searchTemplate = location.search + '?' + pageParameter;
    } else {
      searchTemplate = urlEncodedParameters.match(regex)
        ? urlEncodedParameters.replace(regex, pageParameter)
        : urlEncodedParameters + '&' + pageParameter;
    }
    const pathTemplate =
      escapeUrlForPaginatorTemplate(location.pathname) + searchTemplate;

    return (
      <Paginator
        pathTemplate={pathTemplate}
        currentPage={currentPage}
        lastPage={lastPage}
        pagesToShow={5}
        onClick={this.scrollToTop}
      />
    );
  };

  redirectToProduct = (product) => {
    const { routeService } = this.props;
    const path =
      product.class === ProductClass.MULTI_CHILD
        ? product.pathWithActiveProductId(product.id)
        : product.path;

    const pathToNavigate = routeService.getProductPath(product, path);

    return <Redirect to={pathToNavigate} />;
  };

  scrollToTop = () => {
    scrollToElementById(ANCHOR_ID, { behavior: 'instant' });
  };

  toggleShowAll = () => {
    this.setState({ showAllFilters: !this.state.showAllFilters });
  };

  render() {
    const {
      configStore,
      productStore,
      fixedParams,
      name,
      showSort,
      cardSettings,
      redirectOnSingleMatch,
      showCategoryFilter,
      productParams,
      showCategoryHeaders,
      advancedSearchPage
    } = this.props;

    const { showAllFilters } = this.state;
    if (!productStore || !fixedParams) {
      return;
    }

    return (
      <div className="MainProductList">
        <ScrollableAnchor id={ANCHOR_ID} />
        <ContentForState
          state={this.getCurrentSearchState()}
          forLoaded={() => {
            const searchResult = this.getCurrentSearchResult();
            const products = searchResult.data;
            const hasProducts = products && products.length > 0;

            const filters =
              searchResult && searchResult.available_filters
                ? searchResult.available_filters.filter((filter) => {
                    if (filter.key === 'cross_categories') {
                      return showCategoryFilter;
                    }
                    return !has(fixedParams, filter.key);
                  })
                : [];
            const countOfProducts = searchResult ? searchResult.total : 0;
            const activeFilters = this.getActiveFilters();

            const shouldRedirectToProduct =
              redirectOnSingleMatch &&
              products.length === 1 &&
              countOfProducts === 1 &&
              activeFilters.length === 0;

            if (shouldRedirectToProduct) {
              return this.redirectToProduct(head(products));
            }

            // In stock filter is shown separately
            const [inStockFilters, restOfFilters] = partition(filters, [
              'key',
              IN_STOCK_FILTER_KEY
            ]);
            const inStockFilter = head(inStockFilters);
            const restOfActiveFilters = activeFilters.filter(
              (activeFilter) => activeFilter.key !== IN_STOCK_FILTER_KEY
            );

            const doShowHeaders =
              showCategoryHeaders ||
              this.getSelectedSortOrderBy() === ProductSortOrderBy.CATEGORY ||
              (!this.getSelectedSortOrderBy() &&
                !advancedSearchPage &&
                configStore.productList.defaultSortOption ===
                  ProductSortOrderBy.CATEGORY);

            return (
              <div className="MainProductList__content">
                {activeFilters.length > 0 ? (
                  <RobotsMeta noindex />
                ) : (
                  this.getSEOPagination()
                )}
                {hasProducts && (
                  <FilteringOptions
                    filters={restOfFilters}
                    activeFilters={restOfActiveFilters}
                    onAcceptFilter={this.onAcceptFilter}
                    toggleShowAll={this.toggleShowAll}
                    showAllFilters={showAllFilters}
                    inGrid
                  />
                )}
                <ActiveFilterList
                  activeFilters={restOfActiveFilters}
                  removeFilter={this.removeFilter}
                  clearFilters={this.clearFilters}
                  horizontal
                />
                {(hasProducts || this.state.searchTerm) &&
                  this.renderProductListFilter()}
                {!configStore.siteConfig.isHomePage && (
                  <div className="MainProductList__active-filter-row">
                    <ProductListTools
                      countOfProducts={countOfProducts}
                      style={configStore.productListStyle}
                      onStyleSelect={this.onStyleSelect}
                      onSelectSort={this.onSelectSortOrder}
                      selectedSortOrderBy={this.getSelectedSortOrderBy()}
                      selectedSortOrder={this.getSelectedSortOrder()}
                      showSort={showSort}
                    />
                    {!configStore.hideProductWithZeroQuantity &&
                      configStore.siteConfig.isWebStore && (
                        <BooleanFilter
                          onAccept={this.onInStockAccept}
                          filter={inStockFilter}
                          isSelected={configStore.inStockProductsOnly}
                        />
                      )}
                  </div>
                )}
                {hasProducts ? (
                  <ProductList
                    products={products}
                    name={name}
                    style={configStore.productListStyle}
                    cardSettings={cardSettings}
                    productParams={productParams}
                    showCategoryHeaders={doShowHeaders}
                  />
                ) : (
                  <NoProductsFound />
                )}
                {hasProducts && this.getPaginator()}
              </div>
            );
          }}
        />
      </div>
    );
  }
}

MainProductList.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  fixedParams: PropTypes.object.isRequired,
  name: PropTypes.string,
  showSort: PropTypes.bool,
  allSections: PropTypes.bool,
  history: RouterPropTypes.history.isRequired,
  location: RouterPropTypes.location.isRequired,
  redirectOnSingleMatch: PropTypes.bool,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  showCategoryFilter: PropTypes.bool,
  urlOverride: PropTypes.string,
  productListFilterTerm: PropTypes.func,
  clearProductListFilter: PropTypes.func,
  searchTerm: PropTypes.string,
  disableQuickSearch: PropTypes.bool,
  showCategoryHeaders: PropTypes.bool,
  advancedSearchPage: PropTypes.bool
};

MainProductList.defaultProps = {
  showSort: true,
  allSections: false,
  redirectOnSingleMatch: false,
  urlOverride: null,
  disableQuickSearch: false,
  showCategoryHeaders: false,
  advancedSearchPage: false
};

export default withRouter(
  inject('configStore', 'productStore', 'routeService')(MainProductList)
);



// WEBPACK FOOTER //
// ./src/components/product-list/MainProductList/index.jsx